import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube, User, Building, Hash } from 'lucide-react';
import XIcon from './icons/XIcon';
import DiscordIcon from './icons/DiscordIcon';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { getLogoSrc } from '../constants/brandAssets';

const Footer = () => {
  const { siteSettings } = useSiteSettings();
  return (
    <footer className="bg-slate-950 border-t border-slate-800">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-6">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center relative">
                <img
                  src={getLogoSrc(siteSettings)}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-bold text-white">{siteSettings.site_title}</span>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              3 yıldır güvenilir hosting ve sunucu çözümleri sunuyoruz.
            </p>
            <div className="flex gap-4">
              {siteSettings.social_facebook && (
                <a href={siteSettings.social_facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-12 h-12 rounded-full bg-slate-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-600 flex items-center justify-center transition-all duration-300">
                  <Facebook className="w-5 h-5 text-gray-400 hover:text-white" />
                </a>
              )}
              {siteSettings.social_twitter && (
                <a href={siteSettings.social_twitter} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="w-12 h-12 rounded-full bg-slate-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-600 flex items-center justify-center transition-all duration-300">
                  <XIcon className="w-5 h-5 text-gray-400 hover:text-white" />
                </a>
              )}
              {siteSettings.social_instagram && (
                <a href={siteSettings.social_instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-12 h-12 rounded-full bg-slate-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-600 flex items-center justify-center transition-all duration-300">
                  <Instagram className="w-5 h-5 text-gray-400 hover:text-white" />
                </a>
              )}
              {siteSettings.social_youtube && (
                <a href={siteSettings.social_youtube} target="_blank" rel="noopener noreferrer" aria-label="Youtube" className="w-12 h-12 rounded-full bg-slate-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-600 flex items-center justify-center transition-all duration-300">
                  <Youtube className="w-5 h-5 text-gray-400 hover:text-white" />
                </a>
              )}
              {siteSettings.social_linkedin && (
                <a href={siteSettings.social_linkedin} target="_blank" rel="noopener noreferrer" aria-label="Linkedin" className="w-12 h-12 rounded-full bg-slate-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-600 flex items-center justify-center transition-all duration-300">
                  <Linkedin className="w-5 h-5 text-gray-400 hover:text-white" />
                </a>
              )}
              {siteSettings.social_discord && (
                <a href={siteSettings.social_discord} target="_blank" rel="noopener noreferrer" aria-label="Discord" className="w-12 h-12 rounded-full bg-slate-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-600 flex items-center justify-center transition-all duration-300">
                  <DiscordIcon className="w-5 h-5 text-gray-400 hover:text-white" />
                </a>
              )}
            </div>
          </div>

          {/* Hosting */}
          <div>
            <h3 className="text-white font-semibold mb-4">Hosting</h3>
            <ul className="space-y-2">
              <li><Link to="/web-hosting" className="text-gray-400 hover:text-white text-sm transition-colors">Web Hosting</Link></li>
              <li><Link to="/wordpress-hosting" className="text-gray-400 hover:text-white text-sm transition-colors">WordPress Hosting</Link></li>
              <li><Link to="/reseller-hosting" className="text-gray-400 hover:text-white text-sm transition-colors">Reseller Hosting</Link></li>
            </ul>
          </div>

          {/* Sunucu */}
          <div>
            <h3 className="text-white font-semibold mb-4">Sunucu</h3>
            <ul className="space-y-2">
              <li><Link to="/intel-sunucular" className="text-gray-400 hover:text-white text-sm transition-colors">Intel</Link></li>
              <li><Link to="/ryzen-5950x" className="text-gray-400 hover:text-white text-sm transition-colors">Ryzen 9 5950x</Link></li>
              <li><Link to="/ryzen-7950x" className="text-gray-400 hover:text-white text-sm transition-colors">Ryzen 9 7950x</Link></li>
              <li><Link to="/nested-sunucu" className="text-gray-400 hover:text-white text-sm transition-colors">Nested Sunucu</Link></li>
              <li><Link to="/fiziksel-sunucu" className="text-gray-400 hover:text-white text-sm transition-colors">Fiziksel Sunucu</Link></li>
              <li><Link to="/sunucu-barindirma" className="text-gray-400 hover:text-white text-sm transition-colors">Sunucu Barındırma</Link></li>
            </ul>
          </div>

          {/* Oyun Sunucuları */}
          <div>
            <h3 className="text-white font-semibold mb-4">Oyun Sunucuları</h3>
            <ul className="space-y-2">
              <li><Link to="/minecraft" className="text-gray-400 hover:text-white text-sm transition-colors">Minecraft</Link></li>
              <li><Link to="/cs2" className="text-gray-400 hover:text-white text-sm transition-colors">Counter Strike 2</Link></li>
              <li><Link to="/rust" className="text-gray-400 hover:text-white text-sm transition-colors">Rust</Link></li>
              <li><Link to="/unturned" className="text-gray-400 hover:text-white text-sm transition-colors">Unturned</Link></li>
              <li><Link to="/scp" className="text-gray-400 hover:text-white text-sm transition-colors">SCP</Link></li>
              <li><Link to="/fivem" className="text-gray-400 hover:text-white text-sm transition-colors">FiveM</Link></li>
            </ul>
          </div>

          {/* Ürünler */}
          <div>
            <h3 className="text-white font-semibold mb-4">Ürünler</h3>
            <ul className="space-y-2">
              <li><Link to="/paketler" className="text-gray-400 hover:text-white text-sm transition-colors">Cloud Sunucu</Link></li>
              <li><Link to="/paketler?tab=vps" className="text-gray-400 hover:text-white text-sm transition-colors">VPS Sunucu</Link></li>
              <li><Link to="/paketler?tab=shared" className="text-gray-400 hover:text-white text-sm transition-colors">Paylaşımlı Hosting</Link></li>
              <li><Link to="/reseller-hosting" className="text-gray-400 hover:text-white text-sm transition-colors">Reseller Hosting</Link></li>
            </ul>
          </div>

          {/* Destek */}
          <div>
            <h3 className="text-white font-semibold mb-4">Destek</h3>
            <ul className="space-y-2">
              <li><Link to="/blog" className="text-gray-400 hover:text-white text-sm transition-colors">Blog</Link></li>
              <li><Link to="/iletisim" className="text-gray-400 hover:text-white text-sm transition-colors">İletişim</Link></li>
              <li>
                <Link
                  to="/#faq"
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                  onClick={(e) => {
                    // Eğer ana sayfada değilsek, önce ana sayfaya git
                    if (window.location.pathname !== '/') {
                      e.preventDefault();
                      window.location.href = '/#faq';
                    } else {
                      // Ana sayfadaysa, FAQ bölümüne scroll yap
                      e.preventDefault();
                      const faqElement = document.getElementById('faq');
                      if (faqElement) {
                        const faqRect = faqElement.getBoundingClientRect();
                        const currentScrollY = window.scrollY;
                        const targetScrollY = currentScrollY + faqRect.top - 80;

                        window.scrollTo({
                          top: targetScrollY,
                          behavior: 'smooth'
                        });
                      }
                    }
                  }}
                >
                  SSS
                </Link>
              </li>
            </ul>
          </div>

          {/* İletişim */}
          <div className="lg:-ml-4 xl:-ml-8">
            <h3 className="text-white font-semibold mb-4">İletişim</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300">
                <User className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <span className="text-sm">Yetkili: Furkan Ata</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300">
                <Building className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <span className="text-sm">V.D: Bismil Vergi Dairesi Müd.</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300">
                <Hash className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <span className="text-sm">V.N: 092 093 2327</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300">
                <Phone className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <span className="text-sm">0850 840 75 89</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300">
                <Mail className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <span className="text-sm">info@atahost.com</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300">
                <MapPin className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <span className="text-sm">Altıok Mah. Aydınlık Cad. Nejdetbey<br />Apt. Kat:2 No:2, Bismil/Diyarbakır</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © 2026 Keyzuvi Tüm hakları saklıdır.
          </p>
          <div className="flex gap-6">
            <Link to="/gizlilik-politikasi" className="text-gray-400 hover:text-white text-sm transition-colors">Gizlilik Politikası</Link>
            <Link to="/hizmet-sozlesmesi" className="text-gray-400 hover:text-white text-sm transition-colors">Hizmet Sözleşmesi</Link>
            <Link to="/kvkk-metni" className="text-gray-400 hover:text-white text-sm transition-colors">KVKK</Link>
            <Link to="/acik-riza-metni" className="text-gray-400 hover:text-white text-sm transition-colors">Açık Rıza</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default memo(Footer);

